import { AccountChangeLanguage } from '@/domain/usecases/account/account-change-language'
import { i18nTranslationHelper } from '@/i18n'
import { makeRemoteAccountChangeLanguage } from '@/main/factories/usecases/account/remote-account-change-language-factory'
import theme from '@/presentation/assets/styles/theme'
import UserLanguageForm, {
  UserLanguageImperativeType
} from '@/presentation/components/ui/form/user-language-form/user-language-form'
import SpinnerBackdropApp from '@/presentation/components/ui/spinner/spinner-backdrop-app'
import { Alert, Button, useMediaQuery } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'

export type UserChangeLanguageFormValues = {
  language: string
}

export type UserChangeLanguageFormProps = {
  submitOnChange: boolean
}

export default function UserChangeLanguageForm({ submitOnChange }: UserChangeLanguageFormProps) {
  const remoteChangeLanguage = makeRemoteAccountChangeLanguage()
  const languageRef = useRef<UserLanguageImperativeType>()
  const mdDownMatches = useMediaQuery(theme.breakpoints.down('md'))
  const [alertFeedback, setAlertFeedback] = useState<string | undefined>()
  const [error, setError] = useState(false)

  const defaultValues = {
    language: i18nTranslationHelper.getCurrentLanguage()
  }
  const { setValue, trigger, handleSubmit, getValues, watch } =
    useForm<UserChangeLanguageFormValues>({
      mode: 'all',
      defaultValues
    })

  useEffect(() => {
    const { unsubscribe } = watch(() => {
      closeAlertFeedback()
    }, defaultValues)
    return () => unsubscribe()
  }, [watch])

  function closeAlertFeedback() {
    setAlertFeedback(undefined)
    setError(false)
  }

  const handleNewLanguageValueSubmit = ({ language }: UserChangeLanguageFormValues) => {
    setValue('language', language)
    handleSubmit(handleValidFormValues)()
  }

  const onSubmit = () => {
    trigger()
    languageRef?.current?.submit?.()
  }

  const handleValidFormValues = () => {
    refetch()
  }

  const {
    isFetching: isSubmitting,
    refetch,
    remove
  } = useQuery(
    AccountChangeLanguage.Key,
    async () => {
      setError(false)
      const { language } = getValues()
      return remoteChangeLanguage
        .change({ language })
        .then(() => {
          window.location.reload()
        })
        .catch(error => {
          remove()
          setError(true)
          setAlertFeedback(error.message)
        })
    },
    {
      enabled: false,
      staleTime: 250,
      retry: false
    }
  )

  const t = {
    components: i18nTranslationHelper.getTComponents
  }

  return (
    <Fragment>
      <SpinnerBackdropApp
        loading={isSubmitting}
        message={t.components('userChangeLanguageForm.loadingMessage')}
      />
      <Grid xs={12}>
        <UserLanguageForm
          ref={languageRef}
          onSubmit={handleNewLanguageValueSubmit}
          submitOnChange={submitOnChange}
        />
      </Grid>
      <Grid xs={12} textAlign="end" mt={1}>
        {alertFeedback && (
          <Alert
            className="mb-6"
            onClose={closeAlertFeedback}
            severity={error ? 'error' : 'success'}
            variant="filled"
          >
            {alertFeedback}
          </Alert>
        )}
        {!submitOnChange && (
          <Button variant="contained" onClick={onSubmit} fullWidth={mdDownMatches}>
            {t.components('userChangeLanguageForm.mainCta')}
          </Button>
        )}
      </Grid>
    </Fragment>
  )
}
